// Syntax highlighting for a file in the code editors, by its name.
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { StreamLanguage, type StreamParser } from '@codemirror/language';
import { c, cpp, csharp, java, kotlin } from '@codemirror/legacy-modes/mode/clike';
import { css, sCSS } from '@codemirror/legacy-modes/mode/css';
import { dockerFile } from '@codemirror/legacy-modes/mode/dockerfile';
import { go } from '@codemirror/legacy-modes/mode/go';
import { javascript, typescript } from '@codemirror/legacy-modes/mode/javascript';
import { powerShell } from '@codemirror/legacy-modes/mode/powershell';
import { properties } from '@codemirror/legacy-modes/mode/properties';
import { python } from '@codemirror/legacy-modes/mode/python';
import { rust } from '@codemirror/legacy-modes/mode/rust';
import { shell } from '@codemirror/legacy-modes/mode/shell';
import { msSQL } from '@codemirror/legacy-modes/mode/sql';
import { toml } from '@codemirror/legacy-modes/mode/toml';
import { html, xml } from '@codemirror/legacy-modes/mode/xml';
import { yaml } from '@codemirror/legacy-modes/mode/yaml';
import type { Extension } from '@codemirror/state';

const BY_EXTENSION: Record<string, StreamParser<unknown>> = {
  cs: csharp,
  csx: csharp,
  cshtml: html,
  razor: html,
  csproj: xml,
  fsproj: xml,
  vbproj: xml,
  props: xml,
  targets: xml,
  xml: xml,
  config: xml,
  resx: xml,
  xaml: xml,
  svg: xml,
  html: html,
  htm: html,
  js: javascript,
  mjs: javascript,
  cjs: javascript,
  jsx: javascript,
  ts: typescript,
  mts: typescript,
  cts: typescript,
  tsx: typescript,
  css: css,
  scss: sCSS,
  yml: yaml,
  yaml: yaml,
  sql: msSQL,
  sh: shell,
  bash: shell,
  ps1: powerShell,
  psm1: powerShell,
  toml: toml,
  ini: properties,
  editorconfig: properties,
  gitattributes: properties,
  gitignore: properties,
  properties: properties,
  py: python,
  go: go,
  rs: rust,
  java: java,
  kt: kotlin,
  c: c,
  h: c,
  cpp: cpp,
  hpp: cpp,
};

const defined = new Map<StreamParser<unknown>, StreamLanguage<unknown>>();

function stream(parser: StreamParser<unknown>): StreamLanguage<unknown> {
  let language = defined.get(parser);
  if (!language) defined.set(parser, (language = StreamLanguage.define(parser)));
  return language;
}

/** The language to highlight a file with, by its name; none (plain text) for names it does not know. */
export function languageFor(path: string): Extension {
  const name = path.slice(path.lastIndexOf('/') + 1).toLowerCase();
  const extension = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : name;
  if (extension === 'json' || extension === 'json5') return json();
  if (extension === 'md' || extension === 'markdown') return markdown();
  if (name === 'dockerfile' || name.startsWith('dockerfile.')) return stream(dockerFile);
  const parser = BY_EXTENSION[extension];
  return parser ? stream(parser) : [];
}
